import { POSTHOG_KEY } from "@lipsurf/common/constants";
import { safeTelemetry, telemetryId, telemetryError, validTelemetryId, TELEMETRY_VERSION, type TelemetryProperties } from "@lipsurf/common/telemetry";

declare const process: { env: Record<string, string | undefined> };

const internal = process.env.NODE_ENV !== "production";
const key = internal ? process.env.VITE_TELEMETRY_TEST_KEY : POSTHOG_KEY;
let anonymousId: string | undefined;
let signedIn = false;
const browser = /Edg\//.test(navigator.userAgent) ? "edge" : /Firefox\//.test(navigator.userAgent) ? "firefox" : /Chrome\//.test(navigator.userAgent) ? "chrome" : "other";

export function analyticsId(): string {
  if (anonymousId) return anonymousId;
  const linked = new URLSearchParams(location.search).get("anonId");
  let saved: string | null = null;
  try { saved = localStorage.getItem("lipsurf.analyticsId"); } catch { /* memory fallback */ }
  anonymousId = validTelemetryId(linked) ? linked : validTelemetryId(saved) ? saved : telemetryId();
  try { localStorage.setItem("lipsurf.analyticsId", anonymousId); } catch { /* memory fallback */ }
  return anonymousId;
}

export function setAnalyticsAuthState(value: boolean) { signedIn = value; }

export async function track(event: string, props: TelemetryProperties = {}) {
  if (!key) return;
  try {
    await fetch("https://us.i.posthog.com/capture/", {
      method: "POST", credentials: "omit", referrerPolicy: "no-referrer", keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: key, event, properties: {
        ...safeTelemetry(props), distinct_id: analyticsId(),
        telemetry_version: TELEMETRY_VERSION, app_version: process.env.VITE_APP_VERSION || "website",
        browser, source_context: props.source_context || "website",
        auth_state: signedIn ? "signed_in" : "guest", is_internal: internal,
      } }),
    });
  } catch { /* analytics must never interrupt a purchase or survey */ }
}

export function beginCheckout(plan?: plan, term?: Term, sourceContext = "website") {
  const context = { anonId: analyticsId(), attemptId: telemetryId(), browser,
    appVersion: process.env.VITE_APP_VERSION || "website", sourceContext };
  const props = { attempt_id: context.attemptId, plan, term, source_context: sourceContext,
    mode: plan ? "subscription" : "setup" };
  void track("checkoutStarted", props);
  let failed = false;
  return {
    context,
    failure(error: unknown, stage = "session") {
      if (failed) return;
      failed = true;
      void track("checkoutFailed", { ...props, error_code: telemetryError(error), execution_stage: stage });
    },
  };
}

/** The cancel URL is an observed cancellation; absence of a webhook is not. */
export function trackCheckoutReturn() {
  const attempt = new URLSearchParams(location.search).get("checkoutCancelled");
  if (!validTelemetryId(attempt)) return;
  try {
    if (sessionStorage.getItem(`checkoutCancelled:${attempt}`)) return;
    sessionStorage.setItem(`checkoutCancelled:${attempt}`, "1");
  } catch { /* best effort across reloads */ }
  void track("checkoutCancelled", { attempt_id: attempt });
}
